import { useEffect, useRef, useState } from "react";
import {
  Button,
  Divider,
  Field,
  Input,
  InteractionTag,
  InteractionTagPrimary,
  InteractionTagSecondary,
  Persona,
  Spinner,
  Tag,
  TagGroup,
  Textarea,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import {
  useSaveSummary,
  useAddTopic,
  useDeleteTopic,
  useSaveCallContent,
} from "../api/useMutations";
import { formatDateTime, getDurationString } from "../utils";
import type {
  Call,
  CallContentDto,
  CallParticipant,
  TranscriptSession,
} from "../types";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  row: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    "& > *": { flex: 1 },
  },
  topics: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalS,
    alignItems: "center",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
  },
  participantList: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalM,
  },
  addTopicRow: {
    display: "flex",
    gap: tokens.spacingHorizontalXS,
    alignItems: "center",
  },
});

type Analysis = TranscriptSession["transcriptAnalyses"][number];

interface CallInfoTabProps {
  call: Call;
  analysis?: Analysis;
  callComment?: string;
  participants: CallParticipant[];
  isEditing?: boolean;
  saveSignal?: number;
  resetSignal?: number;
  callId?: number;
  contentId?: string;
  meetingId?: string;
  content?: CallContentDto;
}

export default function CallInfoTab({
  call,
  analysis,
  callComment,
  participants,
  isEditing = false,
  saveSignal = 0,
  resetSignal = 0,
  callId = 0,
  contentId,
  meetingId,
  content,
}: CallInfoTabProps) {
  const styles = useStyles();
  const { t } = useTranslation();
  const { meeting, callLink, startedAt, endedAt, status } = call;

  // Editable state
  const [editSummary, setEditSummary] = useState(analysis?.summary ?? "");
  const [editNotes, setEditNotes] = useState(callComment ?? "");
  const [deletedTopicIds, setDeletedTopicIds] = useState<Set<string>>(
    new Set(),
  );
  const [addedTopics, setAddedTopics] = useState<string[]>([]);
  const [newTopicText, setNewTopicText] = useState("");

  // Sync from props when not editing
  const prevResetSignal = useRef(resetSignal);
  useEffect(() => {
    if (resetSignal !== prevResetSignal.current) {
      prevResetSignal.current = resetSignal;
      setEditSummary(analysis?.summary ?? "");
      setEditNotes(callComment ?? "");
      setDeletedTopicIds(new Set());
      setAddedTopics([]);
      setNewTopicText("");
    }
  }, [resetSignal, analysis?.summary, callComment]);

  // Also re-init when entering edit mode
  useEffect(() => {
    if (isEditing) {
      setEditSummary(analysis?.summary ?? "");
      setEditNotes(callComment ?? "");
      setDeletedTopicIds(new Set());
      setAddedTopics([]);
      setNewTopicText("");
    }
  }, [isEditing, analysis?.summary, callComment]);

  // Mutations
  const saveSummary = useSaveSummary(callId, meetingId);
  const addTopic = useAddTopic(callId, meetingId);
  const deleteTopic = useDeleteTopic(callId, meetingId);
  const saveContent = useSaveCallContent(callId, contentId);

  // Keep a stable ref to the save logic so the effect only depends on saveSignal
  const saveRef = useRef(() => {});
  saveRef.current = () => {
    if (analysis?.id) {
      if (editSummary !== (analysis.summary ?? "")) {
        saveSummary.mutate({ analysisId: analysis.id, summary: editSummary });
      }
      for (const topicId of deletedTopicIds) {
        deleteTopic.mutate({ analysisId: analysis.id, topicId });
      }
      for (const topic of addedTopics) {
        addTopic.mutate({ analysisId: analysis.id, topic });
      }
    }

    if (editNotes !== (callComment ?? "")) {
      const updatedComments = [...(content?.comments ?? [])];
      const existingIdx = updatedComments.findIndex((c) => c.stepId === null);
      if (existingIdx >= 0) {
        updatedComments[existingIdx] = {
          ...updatedComments[existingIdx],
          comment: editNotes,
        };
      } else if (editNotes) {
        updatedComments.push({
          id: 0,
          stepId: null,
          comment: editNotes,
          addedAt: new Date().toISOString(),
        });
      }
      saveContent.mutate({
        ...(content ?? {}),
        comments: updatedComments,
        completedActionIds: content?.instructionSetId ? (content.completedActionIds ?? []) : null,
      });
    }
  };

  // Save on signal
  const prevSaveSignal = useRef(saveSignal);
  useEffect(() => {
    if (saveSignal !== prevSaveSignal.current) {
      prevSaveSignal.current = saveSignal;
      saveRef.current();
    }
  }, [saveSignal]);

  const callAnswered =
    status !== "aborted" && status !== "denied" && status !== "busy";

  const visibleTopics = (analysis?.topics ?? []).filter(
    (t) => !deletedTopicIds.has(t.id),
  );

  const handleAddTopic = () => {
    const text = newTopicText.trim();
    if (text) {
      setAddedTopics((prev) => [...prev, text]);
      setNewTopicText("");
    }
  };

  return (
    <div className={styles.container}>
      {meeting ? (
        <>
          <div className={styles.row}>
            <Field label={t('startTime')}>
              <Input
                value={formatDateTime(meeting.startedAt)}
                readOnly
                appearance={isEditing ? "filled-darker" : undefined}
              />
            </Field>
            <Field label={t('duration')}>
              <Input
                value={getDurationString(meeting.startedAt, meeting.endedAt)}
                readOnly
                appearance={isEditing ? "filled-darker" : undefined}
              />
            </Field>
          </div>
          <Field label={t('title')}>
            <Input
              value={meeting.title ?? ""}
              readOnly
              appearance={isEditing ? "filled-darker" : undefined}
            />
          </Field>
          {meeting.description && (
            <Field label={t('description')}>
              <Textarea
                value={meeting.description}
                readOnly
                appearance={isEditing ? "filled-darker" : undefined}
                resize="vertical"
                rows={2}
              />
            </Field>
          )}
        </>
      ) : (
        <>
          <div className={styles.row}>
            <Field label={t('startTime')}>
              <Input
                value={formatDateTime(startedAt || endedAt)}
                readOnly
                appearance={isEditing ? "filled-darker" : undefined}
              />
            </Field>
            {callAnswered && (
              <Field label={t('duration')}>
                <Input
                  value={getDurationString(startedAt, endedAt)}
                  readOnly
                  appearance={isEditing ? "filled-darker" : undefined}
                />
              </Field>
            )}
          </div>
          {callLink?.topic && (
            <Field label={t('topic')}>
              <Input
                value={callLink.topic}
                readOnly
                appearance={isEditing ? "filled-darker" : undefined}
              />
            </Field>
          )}
          {callLink?.companyName && (
            <Field label={t('companyName')}>
              <Input
                value={callLink.companyName}
                readOnly
                appearance={isEditing ? "filled-darker" : undefined}
              />
            </Field>
          )}
          {callLink?.reference && (
            <Field label={t('reference')}>
              <Input
                value={callLink.reference}
                readOnly
                appearance={isEditing ? "filled-darker" : undefined}
              />
            </Field>
          )}
        </>
      )}

      {analysis && !analysis.summary && (analysis.analysisStatus === 'in_progress' || analysis.analysisStatus === 'pending') && (
        <Field label={t('summary')}>
          <Spinner size="tiny" label={t('analysisInProgress')} />
        </Field>
      )}

      {analysis?.summary && (
        <Field label={t('summary')}>
          <Textarea
            value={isEditing ? editSummary : (analysis?.summary ?? "")}
            onChange={
              isEditing ? (_, data) => setEditSummary(data.value) : undefined
            }
            readOnly={!isEditing}
            appearance={!isEditing ? "filled-darker" : undefined}
            resize="vertical"
            rows={3}
          />
        </Field>
      )}

      {analysis?.summary && (visibleTopics.length > 0 || addedTopics.length > 0 || isEditing) && (
        <Field label={t('topics')}>
          <div className={styles.topics}>
            {isEditing ? (
              <TagGroup
                onDismiss={(_e, data) => {
                  const value = data.value;
                  if (typeof value === "string" && value.startsWith("new-")) {
                    const idx = parseInt(value.slice(4));
                    setAddedTopics((prev) => prev.filter((_, j) => j !== idx));
                  } else if (typeof value === "string") {
                    setDeletedTopicIds((prev) => new Set(prev).add(value));
                  }
                }}
                size="small"
                appearance="brand"
                dismissible
              >
                {visibleTopics.map((topic) => (
                  <InteractionTag key={topic.id} value={topic.id}>
                    <InteractionTagPrimary>{topic.text}</InteractionTagPrimary>
                    <InteractionTagSecondary aria-label={t('remove')} />
                  </InteractionTag>
                ))}
                {addedTopics.map((text, i) => (
                  <InteractionTag key={`new-${i}`} value={`new-${i}`}>
                    <InteractionTagPrimary>{text}</InteractionTagPrimary>
                    <InteractionTagSecondary aria-label={t('remove')} />
                  </InteractionTag>
                ))}
              </TagGroup>
            ) : (
              <>
                {visibleTopics.map((topic) => (
                  <Tag key={topic.id} size="small" appearance="brand">
                    {topic.text}
                  </Tag>
                ))}
              </>
            )}
            {isEditing && (
              <div className={styles.addTopicRow}>
                <Input
                  size="small"
                  placeholder={t('addTopic')}
                  value={newTopicText}
                  onChange={(_, data) => setNewTopicText(data.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddTopic()}
                />
                <Button
                  size="small"
                  appearance="subtle"
                  onClick={handleAddTopic}
                >
                  {t('add')}
                </Button>
              </div>
            )}
          </div>
        </Field>
      )}

      {(callComment || editNotes || isEditing) && (
        <Field label={t('notes')}>
          <Textarea
            value={isEditing ? editNotes : (callComment ?? "")}
            onChange={
              isEditing ? (_, data) => setEditNotes(data.value) : undefined
            }
            readOnly={!isEditing}
            appearance={!isEditing ? "filled-darker" : undefined}
            resize="vertical"
            rows={2}
          />
        </Field>
      )}

      {participants.length > 0 && (
        <>
          <Divider />
          <div className={styles.section}>
            <h2 style={{ fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightSemibold, fontFamily: tokens.fontFamilyBase, margin: 0 }}>{t('participants')}</h2>
            <div className={styles.participantList}>
              {participants.map((participant) => {
                const { id, name, email, role, deleted } = participant.user;
                const isWebUser = role === "guest";

                let secondaryText: string;
                if (deleted) {
                  secondaryText = t('deletedUser');
                } else if (isWebUser) {
                  secondaryText = t('webClientUser');
                } else {
                  secondaryText = email;
                }

                return (
                  <Persona
                    key={id}
                    name={name}
                    secondaryText={secondaryText}
                    size="medium"
                  />
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
